import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  alpha,
  useTheme,
  Link,
  Stack
} from '@mui/material';
import {
  Group as GroupIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  People as PeopleIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

export default function HomePage() {
  const navigate = useNavigate();
  const theme = useTheme();

  const features = [
    {
      icon: <GroupIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
      title: 'Private Groups',
      description: 'Create exclusive circles for your inner crew. Keep plans private and secure.',
      comingSoon: false
    },
    {
      icon: <EventIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
      title: 'Seamless Planning',
      description: 'Coordinate events effortlessly. Track RSVPs and details in real-time.',
      comingSoon: true
    },
    {
      icon: <CalendarIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
      title: 'Smart Availability',
      description: 'Instantly see when everyone is free. No more back-and-forth messaging.',
      comingSoon: true
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
      title: 'Bank-Grade Security',
      description: 'Your data is protected with top-tier encryption and authentication.',
      comingSoon: false
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
      title: 'Lightning Fast',
      description: 'Built for speed. Experience zero lag as you plan your next big event.',
      comingSoon: false
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
      title: 'Collaborative',
      description: 'Plan together. Let everyone have a say in the perfect time and place.',
      comingSoon: true
    }
  ];

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        bgcolor: '#0a0a0a',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {/* Left Column: Hero */}
      <Box
        sx={{
          width: { xs: '100%', md: '38%' },
          flexShrink: 0,
          height: { xs: 'auto', md: '100%' },
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#000',
          borderRight: { md: `1px solid ${alpha('#fff', 0.1)}` },
          borderBottom: { xs: `1px solid ${alpha('#fff', 0.1)}`, md: 'none' },
        }}
      >
        {/* Background gradient blob */}
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            left: '-20%',
            width: '140%',
            height: '140%',
            background: `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.dark, 0.3)}, transparent 70%)`,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            p: { xs: 4, md: 6, lg: 8 },
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ mb: 6 }}>
            <Chip
              label="v2.0 Now Available"
              size="small"
              sx={{
                mb: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.light,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                fontWeight: 600,
              }}
            />
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '3rem', md: '4rem', lg: '4.5rem' },
                fontWeight: 800,
                lineHeight: 1.1,
                mb: 3,
                background: `linear-gradient(135deg, #fff 0%, ${alpha('#fff', 0.7)} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.03em',
              }}
            >
              Plan Parties<br />
              <Box component="span" sx={{ color: theme.palette.primary.main, WebkitTextFillColor: 'initial' }}>
                Like a Pro.
              </Box>
            </Typography>

            <Typography
              variant="h5"
              sx={{
                mb: 5,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                fontWeight: 400,
                lineHeight: 1.6,
                color: alpha('#fff', 0.7),
                maxWidth: '480px',
              }}
            >
              The ultimate tool for coordinating private groups. Sync schedules, track RSVPs, and make it happen.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/signin')}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.8,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 8px 30px -4px ${alpha(theme.palette.primary.main, 0.5)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 40px -4px ${alpha(theme.palette.primary.main, 0.6)}`,
                  },
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Box>

          <Box sx={{ mt: 'auto', pt: 4, borderTop: `1px solid ${alpha('#fff', 0.1)}` }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.4) }}>
                © {new Date().getFullYear()} PartyPlanner
              </Typography>
              <Link href="#" underline="none" sx={{ color: alpha('#fff', 0.4), fontSize: '0.75rem', '&:hover': { color: '#fff' } }}>Privacy</Link>
              <Link href="#" underline="none" sx={{ color: alpha('#fff', 0.4), fontSize: '0.75rem', '&:hover': { color: '#fff' } }}>Terms</Link>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Right Column: Feature cards */}
      <Box
        sx={{
          flex: 1,
          height: { xs: 'auto', md: '100%' },
          overflowY: 'auto',
          p: { xs: 4, md: 6, lg: 8 },
          bgcolor: '#0a0a0a',
          backgroundImage: `
            radial-gradient(at 100% 0%, ${alpha(theme.palette.primary.dark, 0.15)} 0px, transparent 50%),
            radial-gradient(at 0% 100%, ${alpha(theme.palette.secondary.dark, 0.15)} 0px, transparent 50%)
          `,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 3,
            width: '100%',
            maxWidth: '900px',
            mx: 'auto',
          }}
        >
          {features.map((feature, index) => (
            <Card
              key={index}
              sx={{
                background: alpha('#fff', 0.03),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha('#fff', 0.08)}`,
                borderRadius: '24px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                overflow: 'visible',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  background: alpha('#fff', 0.05),
                  borderColor: alpha(feature.color, 0.3),
                  boxShadow: `0 20px 40px -10px ${alpha('#000', 0.5)}`,
                },
              }}
            >
              <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '20px',
                    background: `linear-gradient(135deg, ${alpha(feature.color, 0.2)} 0%, ${alpha(feature.color, 0.05)} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: feature.color,
                    mb: 3,
                    border: `1px solid ${alpha(feature.color, 0.1)}`,
                  }}
                >
                  {feature.icon}
                </Box>

                <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mb: 1.5, color: '#fff' }}>
                  {feature.title}
                </Typography>

                <Typography variant="body1" sx={{ color: alpha('#fff', 0.6), lineHeight: 1.6, mb: 3, flexGrow: 1 }}>
                  {feature.description}
                </Typography>

                {feature.comingSoon && (
                  <Box>
                    <Chip
                      label="Coming Soon"
                      size="small"
                      sx={{
                        backgroundColor: alpha(feature.color, 0.1),
                        color: feature.color,
                        border: `1px solid ${alpha(feature.color, 0.2)}`,
                        fontWeight: 600,
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
